// Pure prompt-to-filter responder for the Prospecting prototype.
// Returns { reply, toolCalls, suggestionRefresh } based on lightweight regex patterns
// over the user's message + the current search context. No LLM, no network.

const COUNTRY_PATTERNS = [
  { match: /\b(denmark|danish|copenhagen|aarhus|dk)\b/i, value: 'Denmark' },
  { match: /\b(sweden|swedish|stockholm|gothenburg|se)\b/i, value: 'Sweden' },
  { match: /\b(norway|norwegian|oslo|no)\b/i, value: 'Norway' },
  { match: /\b(germany|german|berlin|munich|de)\b/i, value: 'Germany' },
  { match: /\b(spain|spanish|madrid|barcelona|es)\b/i, value: 'Spain' },
  { match: /\b(united kingdom|uk|britain|british|london|england)\b/i, value: 'United Kingdom' },
  { match: /\b(united states|usa|us|america|american|new york|san francisco|sf)\b/i, value: 'United States' },
  { match: /\b(netherlands|dutch|amsterdam|nl)\b/i, value: 'Netherlands' },
];

const INDUSTRY_PATTERNS = [
  { match: /\b(dentist|dentists|dental|dental clinic|dental clinics)\b/i, value: 'Dentists' },
  { match: /\b(saas|software|b2b software|enterprise software)\b/i, value: 'SaaS' },
  { match: /\b(fintech|finance|financial|banking|payments)\b/i, value: 'Fintech' },
  { match: /\b(e[- ]?commerce|retail|d2c|dtc|consumer)\b/i, value: 'E-commerce' },
  { match: /\b(healthcare|health|medical|biotech)\b/i, value: 'Healthcare' },
  { match: /\b(manufactur|industrial)\b/i, value: 'Manufacturing' },
  { match: /\b(marketing agency|agency|consultancy|consulting)\b/i, value: 'Marketing Agency' },
];

const SIZE_PATTERNS = [
  { match: /\b(tiny|micro|solo)\b/i, value: ['1-10'] },
  { match: /\b(startup|small companies|small businesses|smb|seed|small team)\b/i, value: ['1-10', '11-50'] },
  { match: /\b(scale[- ]?up|mid[- ]?market|growing companies|growing teams)\b/i, value: ['51-200', '201-1000'] },
  { match: /\b(enterprise|large companies|big companies|corporate)\b/i, value: ['1000+'] },
];

const SENIORITY_PATTERNS = [
  { match: /\b(founder|founders|ceo|cmo|cfo|coo|cto|c-level|chief|co-founder)\b/i, value: 'C-level', label: 'C-level' },
  { match: /\b(vp|vice president|vps)\b/i, value: 'VP', label: 'VPs' },
  { match: /\b(director|directors|head of)\b/i, value: 'Director', label: 'Directors' },
  { match: /\b(manager|managers|lead)\b/i, value: 'Manager', label: 'Managers' },
  { match: /\b(senior|sr\.?)\b/i, value: 'Senior', label: 'Senior ICs' },
];

const ROLE_KEYWORDS = [
  { match: /\b(marketing|growth|demand[- ]?gen|brand)\b/i, value: 'marketing', label: 'Marketing' },
  { match: /\b(sales|account executive|ae|bdr|sdr)\b/i, value: 'sales', label: 'Sales' },
  { match: /\b(product manager|pm|product marketing|product owner)\b/i, value: 'product', label: 'Product' },
  { match: /\b(engineer|engineering|developer|software|technical)\b/i, value: 'engineer', label: 'Engineering' },
  { match: /\b(design|designer|ux|ui)\b/i, value: 'design', label: 'Design' },
  { match: /\b(operations|ops|coo)\b/i, value: 'operation', label: 'Operations' },
  { match: /\b(content|writer|copywriter)\b/i, value: 'content', label: 'Content' },
];

const INSIGHT_PATTERNS = [
  { match: /\b(active on linkedin|posts on linkedin|posts a lot|frequent poster)\b/i, value: 'Frequent poster', label: 'Frequent posters' },
  { match: /\b(many followers|influencer|big following|popular)\b/i, value: 'Many followers', label: 'Has many followers' },
  { match: /\b(recently promoted|new role|just promoted|promotion)\b/i, value: 'Recent promotion', label: 'Recently promoted' },
  { match: /\b(speaker|conference|keynote)\b/i, value: 'Conference speaker', label: 'Conference speakers' },
];

const COMPANY_INSIGHT_PATTERNS = [
  { match: /\b(hiring|growing fast|growing team|expanding team)\b/i, value: 'Actively hiring', label: 'Actively hiring' },
  { match: /\b(expanding|international|going global)\b/i, value: 'Expanding internationally', label: 'Expanding internationally' },
  { match: /\b(profitable|cash flow positive)\b/i, value: 'Profitable', label: 'Profitable' },
];

function pushIfNew(filters, candidate) {
  const exists = filters.some(
    (f) => f.field === candidate.field && f.op === candidate.op && JSON.stringify(f.value) === JSON.stringify(candidate.value)
  );
  if (!exists) filters.push(candidate);
}

function detectFilters(message, searchType, existingFilters) {
  const detected = [];

  // Country (works for both)
  for (const p of COUNTRY_PATTERNS) {
    if (p.match.test(message)) {
      pushIfNew(detected, {
        field: 'country',
        op: 'equals',
        value: p.value,
        label: `Country: ${p.value}`,
      });
    }
  }

  // Industry (works for both)
  for (const p of INDUSTRY_PATTERNS) {
    if (p.match.test(message)) {
      pushIfNew(detected, {
        field: 'industry',
        op: 'contains',
        value: p.value,
        label: `Industry: ${p.value}`,
      });
    }
  }

  // Company size (companies use employeeRange directly; people have companySize)
  for (const p of SIZE_PATTERNS) {
    if (p.match.test(message)) {
      pushIfNew(detected, {
        field: searchType === 'companies' ? 'employeeRange' : 'companySize',
        op: 'in',
        value: p.value,
        label: `Size: ${p.value.join(' / ')}`,
      });
    }
  }

  if (searchType === 'people') {
    // Seniority
    for (const p of SENIORITY_PATTERNS) {
      if (p.match.test(message)) {
        pushIfNew(detected, {
          field: 'seniority',
          op: 'equals',
          value: p.value,
          label: `Seniority: ${p.label}`,
        });
        break; // pick the most specific only
      }
    }
    // Role keyword
    for (const p of ROLE_KEYWORDS) {
      if (p.match.test(message)) {
        pushIfNew(detected, {
          field: 'role',
          op: 'contains',
          value: p.value,
          label: `Role: ${p.label}`,
        });
        break;
      }
    }
    // People insights
    for (const p of INSIGHT_PATTERNS) {
      if (p.match.test(message)) {
        pushIfNew(detected, {
          field: 'insights',
          op: 'hasInsight',
          value: p.value,
          label: p.label,
        });
      }
    }
  } else {
    // Company insights
    for (const p of COMPANY_INSIGHT_PATTERNS) {
      if (p.match.test(message)) {
        pushIfNew(detected, {
          field: 'insights',
          op: 'hasInsight',
          value: p.value,
          label: p.label,
        });
      }
    }
    // Recently funded shortcut
    if (/\b(recently funded|funded|funding|raised|just raised|series [abc]|just closed)\b/i.test(message)) {
      pushIfNew(detected, {
        field: 'recentFunding',
        op: 'equals',
        value: true,
        label: 'Recently funded',
      });
    }
  }

  // Don't double-add filters that already exist on the search
  return detected.filter(
    (d) => !existingFilters.some(
      (f) => f.field === d.field && f.op === d.op && JSON.stringify(f.value) === JSON.stringify(d.value)
    )
  );
}

function summariseAppliedFilters(filters) {
  if (filters.length === 0) return '';
  if (filters.length === 1) return filters[0].label.toLowerCase();
  return filters.slice(0, -1).map((f) => f.label.toLowerCase()).join(', ')
    + ' and ' + filters[filters.length - 1].label.toLowerCase();
}

function buildFallback(userMessage) {
  // Pull the longest noun-ish phrase from the message as a guess.
  const trimmed = userMessage.trim().replace(/[?.!]+$/, '');
  const segment = trimmed.split(/[,;]/)[0].trim();
  return segment.length > 0 ? segment : 'your description';
}

export function respondTo({ userMessage, search }) {
  const filters = detectFilters(userMessage, search.type, search.filters);

  if (filters.length === 0) {
    // Fallback — never silent. Suggest the user be more specific.
    const guess = buildFallback(userMessage);
    return {
      reply: `I couldn't pin down a filter from "${guess}". Try mentioning a country, industry, role, or seniority — e.g. "marketing directors in Denmark" or "fintech startups that recently raised".`,
      toolCalls: [],
    };
  }

  const reply =
    filters.length === 1
      ? `Narrowed the list to ${summariseAppliedFilters(filters)}.`
      : `Applied ${filters.length} filters: ${summariseAppliedFilters(filters)}.`;

  return {
    reply,
    toolCalls: filters.map((f) => ({ name: 'apply_filter', args: f })),
  };
}

const COMPANY_SUGGESTIONS = [
  'Find SaaS companies in Denmark',
  'Find fintech companies in Germany',
  'Find e-commerce companies in the Nordics',
  'Find healthcare companies in Sweden',
  'Find marketing agencies in the United Kingdom',
  'Find manufacturing companies in the Netherlands',
];

function findIcpFacet(icp, key, fallback) {
  return icp?.facets?.find((facet) => facet.key === key)?.value || fallback;
}

function getIcpSuggestions(search) {
  if (!search.icp) return [];

  const geography = findIcpFacet(search.icp, 'geography', 'your target geography');
  const industry = findIcpFacet(search.icp, 'industry', 'this industry');
  const normalizedIndustry = industry.toLowerCase();
  return [
    `Find ${normalizedIndustry} companies in ${geography}`,
    `Find companies in ${geography} in the ${normalizedIndustry} industry`,
    `Find companies in ${geography} within ${normalizedIndustry}`,
  ];
}

export function getSuggestions(search) {
  const icpSuggestions = getIcpSuggestions(search);
  if (icpSuggestions.length > 0) return icpSuggestions.slice(0, 3);

  const pool = COMPANY_SUGGESTIONS;
  // Rotate based on number of applied filters so the user sees fresh prompts after each narrowing.
  const offset = search.filters.length;
  return Array.from({ length: 3 }, (_, i) => pool[(offset + i) % pool.length]);
}
